import {
  getAllFolders,
  getAllPlaylists,
  getContentsByCompany,
  getFolderById,
} from 'apis';
import Button, { ButtonVariants } from 'components/button/button';
import ClientNameAndFolderPath from 'components/client-name-and-folder-path/clientNameAndFolderPath';
import Folders from 'components/folders/folders';
import Modal, { ModalVariants } from 'components/modal/modal';
import PlaylistTable from 'components/playlists/playlist-table/playlistTable';
import Playlists, {
  PlaylistVariants,
} from 'components/playlists/playlists/playlists';
import Search from 'components/search/search';
import { arrowRightWhiteIcon, ROUTES, TEMP_COLUMNS } from 'constants/index';
import { FolderType } from 'enums';
import React, { useCallback, useEffect, useState } from 'react';
// import { toaster } from 'helpers';
import { useLocation } from 'react-router-dom';
import { useStore } from 'store';
import {
  contentPageActions,
  homePageActions,
  layoutActions,
  playlistPageActions,
} from 'store/actions';
import { iState } from 'types';
import styles from './assignContentModal.module.scss';
import ContentList from './content-list/contentList';
import PlaylistContentPopover from './playlist-content-popover/playlistContentPopover';

interface iProps {
  className?: string;
}

const PLAYLIST = 'Playlists';
const CONTENT = 'Content';

function AssignContentModal(props: iProps): JSX.Element {
  const { className } = props;
  const { pathname } = useLocation();
  const [searchKey, setSearchKey] = useState<string>('');
  const [view, setView] = useState(
    (pathname === ROUTES.playlist && CONTENT) || PLAYLIST
  );
  const [state, dispatch]: [iState, any] = useStore();
  const { contentToAddToPlaylist, playlists } = state.playlistsPage || {};
  // console.log({ searchKey });

  const {
    assignModalContentActiveFolder,
    assignModalContentFolders,
    isShowAssignContentOrPlaylist,
  } = state.layout || {};
  const { contents } = state.contentPage;
  const { companyData, contentToAssignScreen, playlistToAssignScreen } =
    state.homePage || {};

  const { id: folderId } = assignModalContentActiveFolder || {};
  const { id: companyId } = companyData || {};

  const [checked, setChecked] = useState({
    showAll: false,
    image: false,
    videos: false,
    webPages: false,
  });

  useEffect(() => {
    dispatch(homePageActions.setContentToAssignToScreen(''));
    dispatch(homePageActions.setPlaylistToAssignToScreen(''));
  }, []);

  const getContents = useCallback(async () => {
    if (!companyId) return;
    const response = await getContentsByCompany({
      company: companyId,
      folder: folderId || '',
    });
    if (response) {
      dispatch(contentPageActions.setContents(response));
    }
  }, [companyId, dispatch, folderId]);

  useEffect(() => {
    if (isShowAssignContentOrPlaylist) getContents();
    else {
      setSearchKey('');
    }
  }, [folderId, getContents, isShowAssignContentOrPlaylist]);

  const assignHandler = useCallback(() => {
    if (pathname === ROUTES.playlist) {
      dispatch(playlistPageActions.invokeUpdatePlaylist(true));
      dispatch(layoutActions.toggleModal());
      return;
    }
    if (pathname === ROUTES.screenDetails) {
      dispatch(homePageActions.invokeUpdateScreen(true));
    }
  }, [dispatch, pathname]);

  const getFoldersByIdHandler = useCallback(
    async (activeFolderId) => {
      if (!isShowAssignContentOrPlaylist) {
        return;
      }
      if (!activeFolderId) {
        return;
      }
      const response = await getFolderById({ id: activeFolderId }, () => {});
      if (response && response.subFolders) {
        const { subFolders, parentFolders } = response;
        dispatch(layoutActions.setAssignModalContentFolders(subFolders || []));
        dispatch(
          layoutActions.setAssignModalContentsFolderHierarchy(
            [...parentFolders].reverse()
          )
        );
      }
    },
    [dispatch, isShowAssignContentOrPlaylist]
  );

  const getAllFoldersHandler = useCallback(async () => {
    if (!companyId) {
      // toaster.error('Company data not found');
      return;
    }
    const folderType =
      (view === CONTENT && FolderType.CONTENT) ||
      (view === PLAYLIST && FolderType.PLAYLIST);
    if (!folderType) return;
    const response = await getAllFolders(
      {
        companyId,
        folderType,
      },
      () => {}
    );

    if (response) {
      dispatch(layoutActions.setAssignModalContentFolders(response));
    }
  }, [companyId, dispatch, view]);

  useEffect(() => {
    if (isShowAssignContentOrPlaylist && !folderId) getAllFoldersHandler();
  }, [getAllFoldersHandler, isShowAssignContentOrPlaylist, folderId, view]);

  useEffect(() => {
    getFoldersByIdHandler(folderId);
  }, [folderId, getFoldersByIdHandler]);

  const getPlaylists = useCallback(async () => {
    if (!companyId) return;

    const response = await getAllPlaylists(
      {
        companyId,
        folderId: folderId || null,
      },
      () => {}
    );
    if (response) {
      dispatch(playlistPageActions.setPlaylist(response));
      dispatch(playlistPageActions.fetchPlaylistData(false));
    }
  }, [companyId, dispatch, folderId]);

  useEffect(() => {
    if (isShowAssignContentOrPlaylist) getPlaylists();
  }, [getPlaylists, isShowAssignContentOrPlaylist]);

  const onCancel = useCallback(() => {
    dispatch(layoutActions.toggleModal());
    dispatch(layoutActions.setAssignModalContentActiveFolders(null));
    dispatch(layoutActions.setAssignModalContentFolders([]));
    dispatch(homePageActions.setContentToAssignToScreen(''));
    dispatch(homePageActions.setPlaylistToAssignToScreen(''));
    dispatch(playlistPageActions.clearAddContentToPlaylist());
    setSearchKey('');
  }, [dispatch]);

  const setFilterToInitialStates = useCallback(() => {
    setChecked({
      showAll: false,
      image: false,
      videos: false,
      webPages: false,
    });
  }, []);
  const playlistData =
    searchKey !== ''
      ? playlists.filter((i) =>
          i.name.toLocaleLowerCase().includes(searchKey.toLocaleLowerCase())
        )
      : playlists;

  const contentsData =
    searchKey !== ''
      ? contents.filter((i) =>
          i.name.toLocaleLowerCase().includes(searchKey.toLocaleLowerCase())
        )
      : contents;

  return (
    <Modal
      onSearch={setSearchKey}
      isOpen={isShowAssignContentOrPlaylist}
      variant={
        view === PLAYLIST
          ? ModalVariants.AddContentPlaylist
          : ModalVariants.AddContentToPlaylist
      }
      className={styles.addContentModal}
      btnName="Assign content"
      heading={<PlaylistContentPopover setView={setView} />}
      submitBtn={
        <Button
          className={styles.assignContentBtn}
          variant={ButtonVariants.SmallStd}
          btnName="Assign content"
          icon={arrowRightWhiteIcon}
          iconPosition="right"
          // isDisabled={
          //   contentToAssignScreen !== '' || contentToAddToPlaylist?.length === 0
          // }
          isDisabled={
            contentToAssignScreen === '' &&
            playlistToAssignScreen === '' &&
            contentToAddToPlaylist?.length === 0
          }
          onClick={assignHandler}
        />
      }
      searchKeyword={searchKey}
      checked={checked}
      setChecked={setChecked}
      submit={() => {}}
      onClose={onCancel}
    >
      <div className={`${className} ${styles.assignContentWrapper}`}>
        <div className={styles.searchBar}>
          <Search isOpenState onChange={setSearchKey} />
        </div>
        {/* {assignModalContentFolders?.length > 0 && ( */}
        <ClientNameAndFolderPath
          className={styles.clientNameAndFolderName}
          username="All Folders"
          setChecked={setFilterToInitialStates}
        />
        {/* // )} */}
        {assignModalContentFolders?.length > 0 && (
          <Folders
            className={styles.folderListing}
            data={assignModalContentFolders}
            openFolder={(folder) => {
              dispatch(
                layoutActions.setAssignModalContentActiveFolders(folder)
              );
            }}
            setChecked={setFilterToInitialStates}
          />
        )}
        {view === PLAYLIST &&
          (playlistData?.length === 0 ? (
            <p className={`${styles.noPlaylistsFound} ${styles.desktop}`}>
              {searchKey !== ''
                ? 'Result not found'
                : 'No playlist exists. You can create a playlist.'}
            </p>
          ) : (
            <PlaylistTable
              className={styles.playlists}
              isShowCheckBox
              columns={TEMP_COLUMNS}
              data={playlistData}
            />
          ))}
        {view === PLAYLIST &&
          (playlistData?.length === 0 ? (
            <p className={`${styles.noPlaylistsFound} ${styles.responsive}`}>
              {searchKey !== ''
                ? 'Result not found'
                : 'No playlist exists. You can upload a playlist.'}
            </p>
          ) : (
            <Playlists
              foundPlaylists={playlistData}
              // foundPlaylists={playlists.filter((i) => i.name.includes(searchKey))}
              variant={PlaylistVariants.AssignContentModal}
            />
          ))}
        {view === CONTENT &&
          (contentsData?.length === 0 ? (
            <p className={`${styles.noPlaylistsFound} `}>
              {searchKey !== ''
                ? 'Result not found'
                : 'No content exists. You can upload content. '}
            </p>
          ) : (
            <ContentList searchKey={searchKey} checked={checked} />
          ))}
      </div>
    </Modal>
  );
}

AssignContentModal.defaultProps = {
  className: '',
};

export default AssignContentModal;
